
import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getUniqueSessionKey, updateSessionActivity } from '@/utils/device-session';

const BulletinsPresence = () => {
  const [count, setCount] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    // Create device-specific presence channel to prevent conflicts
    const sessionKey = getUniqueSessionKey(user?.id);
    const channelName = `bulletins_presence_${sessionKey}`;
    
    console.log('Creating device-specific presence channel:', channelName);
    
    const channel = supabase.channel(channelName);
    let tracked = false;

    // Set up aggregated presence tracking for all users across devices
    const globalPresenceChannel = supabase.channel('bulletins_presence_global');
    
    channel
      .on('presence', { event: 'sync' }, () => {
        updateSessionActivity();
        // Get presence from global channel for accurate count
        const globalState = globalPresenceChannel.presenceState();
        // Count unique user IDs across all devices
        const uniqueUsers = new Set();
        Object.values(globalState).forEach(presences => {
          presences.forEach((presence: any) => {
            if (presence.user_id && presence.user_id !== 'anonymous') {
              uniqueUsers.add(presence.user_id);
            }
          });
        });
        setCount(uniqueUsers.size);
      })
      .on('presence', { event: 'join' }, ({ key }) => {
        console.log('Device session joined:', key);
        updateSessionActivity();
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        console.log('Device session left:', key);
      })
      .subscribe((status) => {
        console.log('Device presence subscription status:', status);
        
        if (status === 'SUBSCRIBED' && !tracked) {
          // Track this specific device session
          channel.track({ 
            user_id: user?.id || 'anonymous',
            session_key: sessionKey,
            online_at: new Date().toISOString(),
            device_info: navigator.userAgent.substring(0, 100)
          })
          .then(() => {
            console.log('Successfully tracked device presence');
            tracked = true;
            updateSessionActivity();
          })
          .catch(error => console.error('Error tracking device presence:', error));
          
          // Also track in global channel for counting
          globalPresenceChannel.track({
            user_id: user?.id || 'anonymous',
            session_key: sessionKey,
            online_at: new Date().toISOString()
          }).catch(error => console.error('Error tracking global presence:', error));
        }
      });

    // Subscribe to global presence for count updates
    globalPresenceChannel.subscribe();

    return () => {
      console.log('Cleaning up device presence channels');
      
      if (tracked) {
        channel.untrack()
          .catch(error => console.error('Error untracking device presence:', error));
        globalPresenceChannel.untrack()
          .catch(error => console.error('Error untracking global presence:', error));
      }
      
      supabase.removeChannel(channel)
        .catch(error => console.error('Error removing device channel:', error));
      supabase.removeChannel(globalPresenceChannel)
        .catch(error => console.error('Error removing global channel:', error));
    };
  }, [user?.id]);

  return (
    <div className="flex items-center">
      <Badge variant="outline" className="text-xs bg-[#1A1F2C]/30 backdrop-blur-sm">
        {count} online
      </Badge>
    </div>
  );
};

export default BulletinsPresence;
